import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { reportsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DownloadSimple, ChartBar, TrendUp, CurrencyDollar, Percent, FilePdf, CaretDown, CaretRight, PaperPlaneTilt, Eye, Wallet } from '@phosphor-icons/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { toast } from 'sonner';

const DISCOUNT_COLORS = ['#0052FF', '#6366F1', '#10B981', '#F59E0B', '#A855F7'];
const METHOD_COLORS = ['#10B981', '#0052FF', '#A855F7', '#F59E0B', '#EF4444', '#6366F1', '#06B6D4', '#94A3B8'];

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstDayOfMonthISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const Reports = () => {
  const { t, language } = useLanguage();
  const [financialReport, setFinancialReport] = useState(null);
  const [forecastReport, setForecastReport] = useState(null);
  const [cashflowReport, setCashflowReport] = useState(null);
  const [discountsReport, setDiscountsReport] = useState(null);
  const [methodsReport, setMethodsReport] = useState(null);
  const [methodsMonthly, setMethodsMonthly] = useState(null);
  const [methodsFrom, setMethodsFrom] = useState(firstDayOfMonthISO());
  const [methodsTo, setMethodsTo] = useState(todayISO());
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [methodsPdfBusy, setMethodsPdfBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedClass, setExpandedClass] = useState(null);
  const [exportingDiscPdf, setExportingDiscPdf] = useState(false);
  const [recapBusy, setRecapBusy] = useState(false);

  useEffect(() => {
    loadReports();
    loadMethods();
  }, []);

  const loadReports = async () => {
    try {
      const [financialRes, forecastRes, cashflowRes, discountsRes] = await Promise.all([
        reportsApi.getFinancial(),
        reportsApi.getForecast(),
        reportsApi.getCashflow(),
        reportsApi.getDiscounts(),
      ]);
      setFinancialReport(financialRes.data);
      setForecastReport(forecastRes.data);
      setCashflowReport(cashflowRes.data);
      setDiscountsReport(discountsRes.data);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMethods = async (from = methodsFrom, to = methodsTo) => {
    setMethodsLoading(true);
    try {
      const [agg, monthly] = await Promise.all([
        reportsApi.getPaymentMethods({ date_from: from || undefined, date_to: to || undefined }),
        reportsApi.getPaymentMethodsMonthly(12),
      ]);
      setMethodsReport(agg.data);
      setMethodsMonthly(monthly.data);
    } catch (e) {
      console.error('Failed to load payment methods report:', e);
      toast.error(language === 'fr' ? 'Erreur chargement rapport' : 'Failed to load report');
    } finally {
      setMethodsLoading(false);
    }
  };

  const handleExportMethodsPdf = async () => {
    setMethodsPdfBusy(true);
    try {
      const res = await reportsApi.exportPaymentMethodsPdf({ date_from: methodsFrom || undefined, date_to: methodsTo || undefined });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paiements_methodes_${methodsFrom || 'all'}_${methodsTo || 'all'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Erreur PDF' : 'PDF failed'));
    } finally {
      setMethodsPdfBusy(false);
    }
  };

  const handleExportDiscountsPdf = async () => {
    setExportingDiscPdf(true);
    try {
      const { data } = await reportsApi.exportDiscountsPdf();
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bilan_remises_${discountsReport?.school_year || ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setExportingDiscPdf(false); }
  };

  const handlePreviewRecap = async () => {
    setRecapBusy(true);
    try {
      const { data } = await reportsApi.previewMonthlyRecap();
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setRecapBusy(false); }
  };

  const handleSendRecap = async () => {
    if (!window.confirm(language === 'fr'
      ? 'Envoyer le récap du mois précédent à l\'administrateur ?'
      : 'Send last-month recap to the admin?')) return;
    setRecapBusy(true);
    try {
      const { data } = await reportsApi.sendMonthlyRecap({});
      if (data.sent) {
        toast.success(
          language === 'fr'
            ? `Récap envoyé à ${data.target}`
            : `Recap sent to ${data.target}`
        );
      } else {
        toast.error(data.error || 'Échec envoi');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setRecapBusy(false); }
  };

  const handleExport = async (type) => {
    try {
      const response = await reportsApi.exportExcel(type);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('success'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('reports')}</h1>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handlePreviewRecap}
            disabled={recapBusy}
            className="border-[#E2E8F0]"
            data-testid="preview-recap-btn"
            title={language === 'fr' ? 'Prévisualiser le récap du mois précédent' : 'Preview last-month recap'}
          >
            <Eye size={18} className="mr-2" />
            {language === 'fr' ? 'Aperçu récap mensuel' : 'Preview monthly recap'}
          </Button>
          <Button
            onClick={handleSendRecap}
            disabled={recapBusy}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="send-recap-btn"
            title={language === 'fr' ? 'Envoyer le récap par email à l\'admin' : 'Email recap to admin'}
          >
            <PaperPlaneTilt size={18} className="mr-2" />
            {recapBusy
              ? (language === 'fr' ? 'Envoi…' : 'Sending…')
              : (language === 'fr' ? 'Envoyer récap admin' : 'Send recap to admin')}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('students')}
            className="border-[#E2E8F0]"
            data-testid="export-students-btn"
          >
            <DownloadSimple size={18} className="mr-2" />
            {language === 'fr' ? 'Export Élèves' : 'Export Students'}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('payments')}
            className="border-[#E2E8F0]"
            data-testid="export-payments-btn"
          >
            <DownloadSimple size={18} className="mr-2" />
            {language === 'fr' ? 'Export Paiements' : 'Export Payments'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="actual" className="space-y-6">
        <TabsList className="bg-[#F1F5F9]">
          <TabsTrigger value="actual" className="data-[state=active]:bg-white" data-testid="tab-actual">
            <ChartBar size={18} className="mr-2" />
            {t('actualReport')}
          </TabsTrigger>
          <TabsTrigger value="forecast" className="data-[state=active]:bg-white" data-testid="tab-forecast">
            <TrendUp size={18} className="mr-2" />
            {t('forecastReport')}
          </TabsTrigger>
          <TabsTrigger value="cashflow" className="data-[state=active]:bg-white" data-testid="tab-cashflow">
            <CurrencyDollar size={18} className="mr-2" />
            {language === 'fr' ? 'Trésorerie mensuelle' : 'Monthly Cashflow'}
          </TabsTrigger>
          <TabsTrigger value="discounts" className="data-[state=active]:bg-white" data-testid="tab-discounts">
            <Percent size={18} className="mr-2" />
            {language === 'fr' ? 'Synthèse des remises' : 'Discounts summary'}
          </TabsTrigger>
          <TabsTrigger value="methods" className="data-[state=active]:bg-white" data-testid="tab-payment-methods">
            <Wallet size={18} className="mr-2" />
            {language === 'fr' ? 'Paiements en ligne' : 'Online payments'}
          </TabsTrigger>
        </TabsList>

        {/* Actual Report */}
        <TabsContent value="actual" className="space-y-6">
          {/* Per-service summaries: Tuition / Cantine / Transport */}
          {[
            { key: 'tuition', label: language === 'fr' ? 'Scolarité' : 'Tuition', color: '#0052FF', data: financialReport?.tuition_summary },
            { key: 'canteen', label: language === 'fr' ? 'Cantine' : 'Canteen', color: '#10B981', data: financialReport?.canteen_summary },
            { key: 'transport', label: language === 'fr' ? 'Transport' : 'Transport', color: '#F59E0B', data: financialReport?.transport_summary },
          ].map((sec) => (
            <Card key={sec.key} className="border border-[#E2E8F0] shadow-none" data-testid={`actual-${sec.key}`}>
              <CardHeader className="pb-3">
                <CardTitle className="font-outfit text-base flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: sec.color }} />
                  {sec.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div>
                    <p className="font-manrope text-xs text-[#64748B] mb-1">{language === 'fr' ? 'Attendu brut' : 'Expected (gross)'}</p>
                    <p className="font-outfit text-lg font-bold text-[#0F172A]" data-testid={`${sec.key}-expected`}>
                      {formatCurrency(sec.data?.total_expected)}
                    </p>
                  </div>
                  <div>
                    <p className="font-manrope text-xs text-[#64748B] mb-1">{language === 'fr' ? '- Remises' : '- Discounts'}</p>
                    <p className="font-outfit text-lg font-bold text-amber-700" data-testid={`${sec.key}-discounts`}>
                      -{formatCurrency(sec.data?.total_discounts)}
                    </p>
                  </div>
                  <div>
                    <p className="font-manrope text-xs text-[#64748B] mb-1">{language === 'fr' ? 'Attendu net' : 'Expected (net)'}</p>
                    <p className="font-outfit text-lg font-bold text-[#0F172A]" data-testid={`${sec.key}-net`}>
                      {formatCurrency(sec.data?.total_expected_net)}
                    </p>
                  </div>
                  <div>
                    <p className="font-manrope text-xs text-[#64748B] mb-1">{language === 'fr' ? 'Payé' : 'Paid'}</p>
                    <p className="font-outfit text-lg font-bold text-[#10B981]" data-testid={`${sec.key}-paid`}>
                      {formatCurrency(sec.data?.total_paid)}
                    </p>
                  </div>
                  <div>
                    <p className="font-manrope text-xs text-[#64748B] mb-1">{language === 'fr' ? 'Solde' : 'Balance'}</p>
                    <p className="font-outfit text-lg font-bold text-[#EF4444]" data-testid={`${sec.key}-balance`}>
                      {formatCurrency(sec.data?.total_balance)}
                    </p>
                  </div>
                </div>
                {/* Progress bar : paid vs net expected */}
                {sec.data?.total_expected_net > 0 && (
                  <div className="mt-3">
                    <div className="w-full h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div
                        className="h-full"
                        style={{
                          width: `${Math.min(100, Math.round((sec.data.total_paid / sec.data.total_expected_net) * 100))}%`,
                          backgroundColor: sec.color,
                        }}
                      />
                    </div>
                    <p className="text-xs font-manrope text-[#64748B] text-right mt-1">
                      {Math.min(100, Math.round((sec.data.total_paid / sec.data.total_expected_net) * 100))}% {language === 'fr' ? 'recouvré' : 'collected'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {/* Payments by Type */}
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader>
              <CardTitle className="font-outfit text-lg">{language === 'fr' ? 'Paiements par type' : 'Payments by Type'}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0]">
                    <TableHead className="font-outfit font-semibold">{t('paymentType')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{language === 'fr' ? 'Nombre' : 'Count'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{t('total')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(financialReport?.payments_by_type || {}).map(([type, data]) => (
                    <TableRow key={type} className="border-[#E2E8F0]">
                      <TableCell className="font-manrope">{t(type)}</TableCell>
                      <TableCell className="font-manrope text-right">{data.count}</TableCell>
                      <TableCell className="font-manrope font-semibold text-right">{formatCurrency(data.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Expenses by Type */}
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader>
              <CardTitle className="font-outfit text-lg">{language === 'fr' ? 'Dépenses par type' : 'Expenses by Type'}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0]">
                    <TableHead className="font-outfit font-semibold">{language === 'fr' ? 'Type de dépense' : 'Expense Type'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{language === 'fr' ? 'Nombre' : 'Count'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{t('total')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(financialReport?.expenses_by_type || {}).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-4 text-[#64748B]">{t('noData')}</TableCell>
                    </TableRow>
                  ) : (
                    Object.entries(financialReport?.expenses_by_type || {}).map(([type, data]) => (
                      <TableRow key={type} className="border-[#E2E8F0]">
                        <TableCell className="font-manrope">{type}</TableCell>
                        <TableCell className="font-manrope text-right">{data.count}</TableCell>
                        <TableCell className="font-manrope font-semibold text-right">{formatCurrency(data.total)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forecast Report */}
        <TabsContent value="forecast" className="space-y-6">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader>
              <CardTitle className="font-outfit text-lg">
                {language === 'fr' ? 'État prévisionnel par classe' : 'Forecast per class'}
                {forecastReport?.school_year && <span className="ml-2 text-sm text-[#64748B]">— {forecastReport.school_year}</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <Card className="border border-[#E2E8F0] shadow-none"><CardContent className="p-3"><p className="text-xs text-[#64748B]">{t('students')}</p><p className="font-outfit text-lg font-bold">{forecastReport?.totals?.student_count || 0}</p></CardContent></Card>
                <Card className="border border-[#E2E8F0] shadow-none"><CardContent className="p-3"><p className="text-xs text-[#64748B]">{t('tuition')}</p><p className="font-outfit text-sm font-bold text-blue-600">{formatCurrency(forecastReport?.totals?.tuition)}</p></CardContent></Card>
                <Card className="border border-[#E2E8F0] shadow-none"><CardContent className="p-3"><p className="text-xs text-[#64748B]">{t('canteen')}</p><p className="font-outfit text-sm font-bold text-green-600">{formatCurrency(forecastReport?.totals?.canteen)}</p></CardContent></Card>
                <Card className="border border-[#E2E8F0] shadow-none"><CardContent className="p-3"><p className="text-xs text-[#64748B]">{t('transport')}</p><p className="font-outfit text-sm font-bold text-yellow-600">{formatCurrency(forecastReport?.totals?.transport)}</p></CardContent></Card>
                <Card className="border border-[#E2E8F0] shadow-none"><CardContent className="p-3"><p className="text-xs text-[#64748B]">{language === 'fr' ? 'Total' : 'Total'}</p><p className="font-outfit text-sm font-bold text-[#0052FF]">{formatCurrency(forecastReport?.totals?.grand_total)}</p></CardContent></Card>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0]">
                    <TableHead className="font-outfit font-semibold">{t('class')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{language === 'fr' ? 'Effectif' : 'Students'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{t('tuition')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{t('canteen')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{t('transport')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{language === 'fr' ? 'Inscription' : 'Registration'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{t('total')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(forecastReport?.by_class || []).length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-4 text-[#64748B]">{t('noData')}</TableCell></TableRow>
                  ) : (
                    forecastReport.by_class.map((row) => (
                      <TableRow key={row.class_name} className="border-[#E2E8F0]" data-testid={`forecast-row-${row.class_name}`}>
                        <TableCell className="font-manrope font-medium">
                          {row.class_name}
                          {row.fees_inherited && (
                            <span
                              data-testid={`inherited-badge-${row.class_name}`}
                              title="Frais hérités de la classe de niveau parente"
                              className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">
                              hérité
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-manrope text-right">{row.student_count}</TableCell>
                        <TableCell className="font-manrope text-right">{formatCurrency(row.tuition_total)}</TableCell>
                        <TableCell className="font-manrope text-right">{formatCurrency(row.canteen_total)}</TableCell>
                        <TableCell className="font-manrope text-right">{formatCurrency(row.transport_total)}</TableCell>
                        <TableCell className="font-manrope text-right">{formatCurrency(row.registration_total)}</TableCell>
                        <TableCell className="font-manrope font-semibold text-right">{formatCurrency(row.line_total)}</TableCell>
                      </TableRow>
                    ))
                  )}
                  {forecastReport?.totals && (
                    <TableRow className="border-[#E2E8F0] bg-[#F8FAFC]">
                      <TableCell className="font-outfit font-bold">{t('total')}</TableCell>
                      <TableCell className="font-outfit font-bold text-right">{forecastReport.totals.student_count}</TableCell>
                      <TableCell className="font-outfit font-bold text-right">{formatCurrency(forecastReport.totals.tuition)}</TableCell>
                      <TableCell className="font-outfit font-bold text-right">{formatCurrency(forecastReport.totals.canteen)}</TableCell>
                      <TableCell className="font-outfit font-bold text-right">{formatCurrency(forecastReport.totals.transport)}</TableCell>
                      <TableCell className="font-outfit font-bold text-right">{formatCurrency(forecastReport.totals.registration)}</TableCell>
                      <TableCell className="font-outfit font-bold text-right text-[#0052FF]">{formatCurrency(forecastReport.totals.grand_total)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cashflow Report */}
        <TabsContent value="cashflow" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border border-[#E2E8F0] shadow-none">
              <CardContent className="p-4"><p className="text-sm text-[#64748B]">{language === 'fr' ? 'Total entrées' : 'Total in'}</p><p className="font-outfit text-xl font-bold text-[#10B981]">{formatCurrency(cashflowReport?.totals?.total_in)}</p></CardContent>
            </Card>
            <Card className="border border-[#E2E8F0] shadow-none">
              <CardContent className="p-4"><p className="text-sm text-[#64748B]">{language === 'fr' ? 'Total sorties' : 'Total out'}</p><p className="font-outfit text-xl font-bold text-[#EF4444]">{formatCurrency(cashflowReport?.totals?.total_out)}</p></CardContent>
            </Card>
            <Card className="border border-[#E2E8F0] shadow-none">
              <CardContent className="p-4"><p className="text-sm text-[#64748B]">{language === 'fr' ? 'Trésorerie nette' : 'Net'}</p><p className={`font-outfit text-xl font-bold ${(cashflowReport?.totals?.net || 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{formatCurrency(cashflowReport?.totals?.net)}</p></CardContent>
            </Card>
          </div>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader>
              <CardTitle className="font-outfit text-lg">{language === 'fr' ? 'Flux mensuels' : 'Monthly cashflow'}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0]">
                    <TableHead className="font-outfit font-semibold">{language === 'fr' ? 'Mois' : 'Month'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{language === 'fr' ? 'Encaissements' : 'Inflows'}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{t('expenses')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{t('salaries')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-right">{language === 'fr' ? 'Net' : 'Net'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(cashflowReport?.rows || []).length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-4 text-[#64748B]">{t('noData')}</TableCell></TableRow>
                  ) : (
                    cashflowReport.rows.map((row) => (
                      <TableRow key={row.month} className="border-[#E2E8F0]" data-testid={`cashflow-row-${row.month}`}>
                        <TableCell className="font-mono">{row.month}</TableCell>
                        <TableCell className="text-right text-[#10B981]">{formatCurrency(row.payments_in)}</TableCell>
                        <TableCell className="text-right text-[#EF4444]">{formatCurrency(row.expenses)}</TableCell>
                        <TableCell className="text-right text-[#EF4444]">{formatCurrency(row.salaries)}</TableCell>
                        <TableCell className={`text-right font-semibold ${row.net >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{formatCurrency(row.net)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Discounts synthesis tab */}
        <TabsContent value="discounts" className="space-y-6" data-testid="discounts-tab-content">
          {!discountsReport ? (
            <p className="text-sm text-[#94A3B8]">{language === 'fr' ? 'Chargement…' : 'Loading…'}</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-outfit text-lg font-bold">
                    {language === 'fr' ? `Bilan des remises — ${discountsReport.school_year}` : `Discounts — ${discountsReport.school_year}`}
                  </h2>
                  <p className="font-manrope text-sm text-[#64748B]">
                    {language === 'fr' ? 'Total accordé' : 'Total granted'} :{' '}
                    <span className="font-bold text-amber-700" data-testid="disc-total">
                      {formatCurrency(discountsReport.totals.total_amount_saved)}
                    </span>
                    {' · '}{discountsReport.totals.discount_lines} {language === 'fr' ? 'ligne(s) de remise' : 'discount line(s)'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleExport('discounts')}
                    data-testid="export-disc-excel"
                  >
                    <DownloadSimple size={16} className="mr-1" /> Excel
                  </Button>
                  <Button
                    onClick={handleExportDiscountsPdf}
                    disabled={exportingDiscPdf}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="export-disc-pdf"
                  >
                    <FilePdf size={16} className="mr-1" />
                    {exportingDiscPdf ? (language === 'fr' ? 'Génération…' : 'Generating…') : 'PDF'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* By type table */}
                <Card className="border border-[#E2E8F0] shadow-none lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="font-outfit text-base">
                      {language === 'fr' ? 'Synthèse par type de frais' : 'Per fee type'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#E2E8F0]">
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">{language === 'fr' ? 'Nb élèves' : 'Students'}</TableHead>
                          <TableHead className="text-right">{language === 'fr' ? '% moyen' : 'Avg %'}</TableHead>
                          <TableHead className="text-right">{language === 'fr' ? 'Total remises' : 'Total saved'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {discountsReport.by_type.map((r, i) => (
                          <TableRow key={r.type} className="border-[#E2E8F0]" data-testid={`disc-type-${r.type}`}>
                            <TableCell className="font-manrope flex items-center gap-2">
                              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: DISCOUNT_COLORS[i % 5] }} />
                              {r.label}
                            </TableCell>
                            <TableCell className="text-right">{r.count}</TableCell>
                            <TableCell className="text-right">{r.avg_percent}%</TableCell>
                            <TableCell className="text-right font-semibold text-amber-700">{formatCurrency(r.amount_saved)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-[#E2E8F0] bg-amber-50">
                          <TableCell className="font-bold">TOTAL</TableCell>
                          <TableCell className="text-right font-bold">{discountsReport.totals.discount_lines}</TableCell>
                          <TableCell className="text-right">—</TableCell>
                          <TableCell className="text-right font-bold text-amber-800">{formatCurrency(discountsReport.totals.total_amount_saved)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Pie chart */}
                <Card className="border border-[#E2E8F0] shadow-none">
                  <CardHeader>
                    <CardTitle className="font-outfit text-base">
                      {language === 'fr' ? 'Répartition' : 'Breakdown'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {discountsReport.totals.total_amount_saved <= 0 ? (
                      <p className="text-sm text-[#94A3B8] py-6 text-center">{language === 'fr' ? 'Aucune remise accordée.' : 'No discounts.'}</p>
                    ) : (
                      <div style={{ width: '100%', height: 260 }} data-testid="disc-pie">
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={discountsReport.by_type.filter(r => r.amount_saved > 0).map(r => ({ name: r.label, value: r.amount_saved }))}
                              dataKey="value" innerRadius={50} outerRadius={90} paddingAngle={2}
                              label={(e) => `${Math.round(e.percent * 100)}%`}
                            >
                              {discountsReport.by_type.filter(r => r.amount_saved > 0).map((_, i) => (
                                <Cell key={i} fill={DISCOUNT_COLORS[i % 5]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v) => formatCurrency(v)} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Top 5 + By class */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border border-[#E2E8F0] shadow-none">
                  <CardHeader>
                    <CardTitle className="font-outfit text-base">
                      {language === 'fr' ? 'Top 5 bénéficiaires' : 'Top 5 beneficiaries'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#E2E8F0]">
                          <TableHead>{language === 'fr' ? 'Élève' : 'Student'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Classe' : 'Class'}</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">%</TableHead>
                          <TableHead className="text-right">{language === 'fr' ? 'Montant' : 'Amount'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {discountsReport.top.length === 0 ? (
                          <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#94A3B8]">—</TableCell></TableRow>
                        ) : discountsReport.top.map((r, i) => (
                          <TableRow key={i} className="border-[#E2E8F0]">
                            <TableCell className="font-manrope">{r.student_name}</TableCell>
                            <TableCell>{r.class}</TableCell>
                            <TableCell>{r.type_label}</TableCell>
                            <TableCell className="text-right">{r.percent}%</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrency(r.amount_saved)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card className="border border-[#E2E8F0] shadow-none">
                  <CardHeader>
                    <CardTitle className="font-outfit text-base">
                      {language === 'fr' ? 'Détail par classe' : 'Per class detail'}
                      <span className="text-xs font-manrope text-[#64748B] ml-2">({language === 'fr' ? 'clic pour étendre' : 'click to expand'})</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {discountsReport.by_class.length === 0 ? (
                      <p className="p-6 text-sm text-[#94A3B8]">—</p>
                    ) : (
                      <div className="divide-y divide-[#E2E8F0] max-h-96 overflow-y-auto">
                        {discountsReport.by_class.map((c, idx) => {
                          const expanded = expandedClass === c.class;
                          return (
                            <div key={idx} data-testid={`disc-class-${c.class}`}>
                              <button
                                className="w-full flex items-center justify-between p-3 hover:bg-[#F8FAFC] text-left"
                                onClick={() => setExpandedClass(expanded ? null : c.class)}
                              >
                                <span className="font-manrope flex items-center gap-2">
                                  {expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                                  <strong>{c.class}</strong>
                                </span>
                                <span className="font-bold text-amber-700">{formatCurrency(c.total_saved)}</span>
                              </button>
                              {expanded && (
                                <div className="px-8 pb-3 text-xs font-manrope space-y-1">
                                  {Object.entries(c.types).map(([type, v]) => (
                                    <div key={type} className="flex justify-between">
                                      <span>{discountsReport.by_type.find(x => x.type === type)?.label || type}</span>
                                      <span>{v.count} élève(s) · <strong>{formatCurrency(v.amount_saved)}</strong></span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Payment methods report */}
        <TabsContent value="methods" className="space-y-6" data-testid="methods-tab-content">
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <CardTitle className="font-outfit text-base flex items-center gap-2">
                    <Wallet size={18} className="text-emerald-600" weight="duotone" />
                    {language === 'fr' ? 'Répartition des paiements par moyen' : 'Payments by method'}
                  </CardTitle>
                  <p className="text-xs text-[#64748B] mt-1">
                    {language === 'fr'
                      ? 'Mobile Money / Cartes / Espèces / Virements — utile pour le rapprochement comptable mensuel.'
                      : 'Mobile Money / Cards / Cash / Transfers — useful for monthly reconciliation.'}
                  </p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#64748B] block mb-1">
                      {language === 'fr' ? 'Du' : 'From'}
                    </label>
                    <input
                      type="date" value={methodsFrom}
                      onChange={(e) => setMethodsFrom(e.target.value)}
                      className="h-9 px-2 border border-[#E2E8F0] rounded-md text-sm"
                      data-testid="methods-date-from"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#64748B] block mb-1">
                      {language === 'fr' ? 'Au' : 'To'}
                    </label>
                    <input
                      type="date" value={methodsTo}
                      onChange={(e) => setMethodsTo(e.target.value)}
                      className="h-9 px-2 border border-[#E2E8F0] rounded-md text-sm"
                      data-testid="methods-date-to"
                    />
                  </div>
                  <Button
                    onClick={() => loadMethods()}
                    disabled={methodsLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                    data-testid="methods-refresh"
                  >
                    {methodsLoading ? '…' : (language === 'fr' ? 'Actualiser' : 'Refresh')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleExportMethodsPdf}
                    disabled={methodsPdfBusy || !methodsReport}
                    className="border-[#B45309] text-[#B45309] hover:bg-[#FEF3C7] h-9"
                    data-testid="methods-export-pdf"
                  >
                    <FilePdf size={16} className="mr-2" />
                    {methodsPdfBusy ? '…' : (language === 'fr' ? 'Export PDF' : 'Export PDF')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!methodsReport ? (
                <p className="text-sm text-[#94A3B8] text-center py-8">
                  {language === 'fr' ? 'Chargement…' : 'Loading...'}
                </p>
              ) : methodsReport.methods.length === 0 ? (
                <p className="text-sm text-[#94A3B8] text-center py-8">
                  {language === 'fr' ? 'Aucun paiement sur cette période.' : 'No payment on this range.'}
                </p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pie */}
                  <div style={{ width: '100%', height: 320 }} data-testid="methods-pie">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={methodsReport.methods}
                          dataKey="total" nameKey="label"
                          outerRadius={110}
                          label={(e) => `${e.label}: ${e.percentage}%`}
                        >
                          {methodsReport.methods.map((_, i) => (
                            <Cell key={i} fill={METHOD_COLORS[i % METHOD_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => `${Number(v).toLocaleString('fr-FR')} FCFA`} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Table */}
                  <div>
                    <div className="bg-emerald-50 rounded-lg p-4 mb-3 grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-[10px] uppercase text-emerald-700/70">{language === 'fr' ? 'Total' : 'Total'}</p>
                        <p className="font-outfit font-bold text-emerald-700">
                          {Number(methodsReport.grand_total || 0).toLocaleString('fr-FR')} FCFA
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-emerald-700/70">{language === 'fr' ? 'Paiements' : 'Payments'}</p>
                        <p className="font-outfit font-bold text-emerald-700">{methodsReport.grand_count}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-emerald-700/70">{language === 'fr' ? 'Moyens' : 'Methods'}</p>
                        <p className="font-outfit font-bold text-emerald-700">{methodsReport.methods.length}</p>
                      </div>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{language === 'fr' ? 'Moyen' : 'Method'}</TableHead>
                          <TableHead className="text-right">{language === 'fr' ? 'Nombre' : 'Count'}</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">%</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {methodsReport.methods.map((m, i) => (
                          <TableRow key={m.method} data-testid={`methods-row-${m.method}`}>
                            <TableCell className="font-manrope">
                              <span
                                className="inline-block w-3 h-3 rounded-full mr-2 align-middle"
                                style={{ backgroundColor: METHOD_COLORS[i % METHOD_COLORS.length] }}
                              />
                              {m.label}
                            </TableCell>
                            <TableCell className="text-right font-manrope">{m.count}</TableCell>
                            <TableCell className="text-right font-manrope font-semibold">
                              {Number(m.total).toLocaleString('fr-FR')} FCFA
                            </TableCell>
                            <TableCell className="text-right font-manrope">{m.percentage.toFixed(2)} %</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly evolution chart (last 12 months) */}
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="font-outfit text-base flex items-center gap-2">
                <TrendUp size={18} className="text-emerald-600" weight="duotone" />
                {language === 'fr' ? 'Évolution mensuelle — 12 derniers mois' : 'Monthly evolution — last 12 months'}
              </CardTitle>
              <p className="text-xs text-[#64748B] mt-1">
                {language === 'fr'
                  ? 'Adoption Mobile Money vs Cartes vs Espèces vs Virements dans le temps.'
                  : 'Mobile Money vs Cards vs Cash vs Transfers over time.'}
              </p>
            </CardHeader>
            <CardContent>
              {!methodsMonthly || methodsMonthly.series.length === 0 ? (
                <p className="text-sm text-[#94A3B8] text-center py-8">
                  {language === 'fr' ? 'Aucune donnée à afficher.' : 'No data.'}
                </p>
              ) : (
                <div style={{ width: '100%', height: 320 }} data-testid="methods-monthly-chart">
                  <ResponsiveContainer>
                    <LineChart
                      data={methodsMonthly.months.map((m, i) => {
                        const row = { month: m.label };
                        methodsMonthly.series.forEach(s => { row[s.label] = s.points[i]; });
                        return row;
                      })}
                      margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
                      <Tooltip formatter={(v) => `${Number(v).toLocaleString('fr-FR')} FCFA`} />
                      <Legend />
                      {methodsMonthly.series.map((s, i) => (
                        <Line
                          key={s.key}
                          type="monotone"
                          dataKey={s.label}
                          stroke={METHOD_COLORS[i % METHOD_COLORS.length]}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
